import { transformObjectToCamelCase } from '@utilities';

import type {
  CustomGroup,
  CustomGroupBreakout,
  CustomGroupBreakoutResponse,
  CustomGroupListResponse,
  CustomGroupPage,
  CustomGroupPageResponse,
  CustomGroupResponse,
  CustomGroupType,
} from '../types';

export const transformDefinitions = (
  breakouts: CustomGroupBreakoutResponse[]
): Record<string, string> => {
  return breakouts.reduce<Record<string, string>>((definitions, { definition, title }) => {
    return { ...definitions, [title]: definition };
  }, {});
};

export const transformCustomGroupBreakouts = (
  breakouts: CustomGroupBreakoutResponse[],
  id: number
): CustomGroupBreakout[] => {
  const transformedBreakouts = transformObjectToCamelCase(breakouts) as Array<
    Omit<CustomGroupBreakout, 'allDefinitions' | 'hasException' | 'id'>
  >;

  return transformedBreakouts.map((breakout) => {
    return {
      allDefinitions: transformDefinitions(breakouts),
      conditionalDefinitionAttributes: breakout.conditionalDefinitionAttributes,
      definition: breakout.definition,
      hasException: (
        breakout.conditionalDefinitionAttributes as typeof breakout.conditionalDefinitionAttributes & {
          hasException?: boolean;
        }
      ).hasException,
      id,
      title: breakout.title,
    };
  });
};

export const transformCustomGroupData = (
  item: CustomGroupListResponse | CustomGroupResponse
): CustomGroup => {
  const transformedItem = transformObjectToCamelCase(item) as Omit<
    CustomGroup,
    'breakouts' | 'definition' | 'isOwner'
  >;
  const parsedDefinition = 'parsed_definition' in item ? item.parsed_definition : undefined;

  return {
    ...transformedItem,
    breakouts: parsedDefinition
      ? transformCustomGroupBreakouts(parsedDefinition, transformedItem.id)
      : null,
    definition: parsedDefinition ? transformDefinitions(parsedDefinition) : null,
    isOwner: !transformedItem.isLocked,
    overlaps: transformedItem.overlaps || {},
  };
};

export const transformCustomGroupsData = (data: CustomGroupPageResponse): CustomGroupPage => {
  return {
    items: data.items.map(transformCustomGroupData),
    page: data.page,
    size: data.size,
    total: data.total,
  };
};

export const getCustomGroupType = (type: CustomGroupType): 'place' | 'product_cg' => {
  const groupTypes: Record<CustomGroupType, 'place' | 'product_cg'> = {
    product: 'product_cg',
    store: 'place',
  };

  return groupTypes[type];
};
